import type { UiState } from 'instantsearch.js'
import qs from 'qs'
import { algoliaTemplatesIndex } from 'algolia/algolia-templates'
import { decodeQueryArray } from 'utils/query'

import { SEARCHABLE_FILTERS } from '../filters-section/constants'

export interface RouteType {
  pathname: string
  query: string
}

const CATEGORY_FALLBACK = 'all'
let lastUIState: UiState

// Returns a slug from the category name. Spaces are replaced by "+" to make the URL easier to read
// and other characters are encoded.
function getCategorySlug(name: string) {
  return name.split(' ').map(encodeURIComponent).join('+')
}

// Returns a name from the category slug. The "+" are replaced by spaces and other characters are decoded.
function getCategoryName(slug: string) {
  return slug.split('+').map(decodeURIComponent).join(' ')
}

export const stateMapping = {
  routeToState(route: RouteType | null): UiState {
    // If the route is null, it means the modal is open, so we return the last UI state
    if (!route) {
      return lastUIState
    }

    const { pathname, query: queryString } = route
    const queryObject = qs.parse(queryString)

    const {
      query = '',
      page,
      sortBy,
      [SEARCHABLE_FILTERS.STYLES.label]: styles,
      [SEARCHABLE_FILTERS.SUBJECT.label]: subject,
      [SEARCHABLE_FILTERS.TOPIC.label]: topic,
      [SEARCHABLE_FILTERS.PRICE.label]: price,
      [SEARCHABLE_FILTERS.THEME.label]: themes,
      [SEARCHABLE_FILTERS.FEATURE.label]: feature,
      tags,
    } = queryObject

    // eslint-disable-next-line prefer-const
    let [category, subcategory] = pathname.split('/')

    // Ignore the category if it matches the fallback value
    if (category === CATEGORY_FALLBACK) {
      category = ''
    }

    const uiState = {
      [algoliaTemplatesIndex]: {
        query: String(query),
        // oxlint-disable-next-line no-negated-condition
        page: !Number(page) ? 1 : Number(page),
        sortBy: sortBy ? String(sortBy) : '',
        configure: {
          hitsPerPage: 50,
        },
        refinementList: {
          // TODO: cleanup
          // [1/2]: pnpm migration notice:
          // these types were failing, although the libs used are on the same versions.
          // without changing prod code, this was the best thing i could come up with to keep
          // the behaviour the same but appease the type gods.
          ...(styles && { styles: decodeQueryArray(styles as string) }),
          ...(subject && { subject: decodeQueryArray(subject as string) }),
          ...(topic && { topic: decodeQueryArray(topic as string) }),
          ...(price && { price: decodeQueryArray(price as string) }),
          ...(themes && { themes: decodeQueryArray(themes as string) }),
          ...(feature && { feature: decodeQueryArray(feature as string) }),
          ...(tags && { tags: decodeQueryArray(tags as string) }),
        },
        ...((category || subcategory) && {
          menu: {
            ...(category && { category: getCategoryName(category) }),
            ...(subcategory && { subcategory: getCategoryName(subcategory) }),
          },
        }),
      },
      // TODO: cleanup
      // [2/2]: pnpm migration notice.
      // these types used to fail, even on staging. i don't know how they passed our CI lol.
      // i am fixing them here as part of the pnpm migration.
      // if you are coming here to work on it, it may be wise to try and remove this type casting
    } as unknown as UiState

    lastUIState = uiState
    return uiState
  },
  stateToRoute(uiState: UiState): RouteType {
    const { query, page, refinementList, sortBy, menu } = uiState?.[algoliaTemplatesIndex] || {}
    let queryParams = {}
    if (refinementList && Object.keys(refinementList)?.length > 0) {
      Object.keys(refinementList).forEach(key => {
        queryParams = {
          ...queryParams,
          [key]: refinementList[key].map(encodeURIComponent),
        }
      })
    }

    const pathname = `${getCategorySlug(
      menu?.category || (menu?.subcategory ? CATEGORY_FALLBACK : ''),
    )}${menu?.subcategory ? '/' : ''}${getCategorySlug(menu?.subcategory || '')}`

    const queryObject = {
      query: query ? query : undefined,
      sortBy: sortBy ? sortBy : undefined,
      page: page && page !== 1 ? page : undefined,
      ...queryParams,
    }

    return {
      pathname: pathname,
      query: qs.stringify(queryObject, { arrayFormat: 'comma' }),
    }
  },
}
